import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'
import { match } from '@/utils/pattern-match-util'
import { OCRScopeWorkWorkspaceSearchViewModel } from '@/features/jobs/scoping/OCRScopeWorkWorkspaceSearchViewModel'
import { type Member } from '@/features/iam/members/Member'
import { catchError, EMPTY, merge, type Observable, switchMap, tap } from 'rxjs'
import { required, validationContext } from 'validx'
import { type PublishedLegendStore } from '@/features/jobs/legends/PublishedLegendStore'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import {
  type LegendTemplate,
  type PublishedLegend,
} from '@/features/jobs/legends/PublishedLegend'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { type Id } from '@/infra/id'
import type {
  ListTeamMembersFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  SearchMembersFn,
  ListTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import type { UserDataType } from '@/features/iam/users/UserData'
import { type TeamMember } from '@/features/iam/teams/TeamMember'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { shareLatest, whileActive, withAbortSignal } from '@/utils/rx-util'
import type {
  Question,
  SelectQuestionOption,
} from '@/features/jobs/questions/questions'
import { type Team } from '@/features/iam/teams/Team'
import type { OCRManualInputAnswer } from '@/features/jobs/scoping/ocr-manual-input'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ACCEPTED_OCR_FILE_TYPES } from '@/features/jobs/scoping/ocr-file-types'

const ALLOWED_OCR_CLIENT_USER_ADMIN_TYPES: UserDataType[] = ['User', 'Admin']

const ALLOWED_OCR_CLIENT_USER_TYPES: UserDataType[] = ['User']

/**
 * Step of the dialog.
 */
export type CreateOCRScopeJobsStep = 'ManageDocuments' | 'ScopeNewJobs'

/**
 * Whether the files are scoped into one job, or a job each.
 */
export type ScopeJobMode = 'Collectively' | 'Individually'

/**
 * Creates scope works from uploaded documents.
 */
export class CreateOCRScopeJobsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The member whose workspace was last selected.
   */
  private _member: Member | null = null

  private readonly _championClientQuery$

  private readonly _supportingClientQuery$

  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  readonly workspaceSelectorVm

  /**
   * Validation context.
   */
  readonly validation

  readonly championClientSearchText = new SearchTextViewModel()

  readonly supportingClientSearchText = new SearchTextViewModel()

  readonly legendScrollProvider = new InfiniteScrollProvider<PublishedLegend>({
    limit: 30,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading legends failed',
        signal,
      )
      return withRetry(() =>
        this._publishedLegendStore.listLegends(
          {
            workspaceId: this.workspace?.id,
            after: paging.after,
            limit: paging.limit,
          },
          signal,
        ),
      )
    },
  })

  readonly championClientScrollProvider = new InfiniteScrollProvider<Member>({
    limit: 20,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading clients failed',
        signal,
      )
      return withRetry(() =>
        this._searchChampionClient(
          this.championClientSearchText.value,
          paging,
          signal,
        ),
      )
    },
  })

  readonly supportingClientScrollProvider = new InfiniteScrollProvider<Member>({
    limit: 20,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading clients failed',
        signal,
      )
      return withRetry(() =>
        this._searchSupportingClient(
          this.supportingClientSearchText.value,
          paging,
          signal,
        ),
      )
    },
  })

  readonly clientTeamMembersScrollProvider =
    new InfiniteScrollProvider<TeamMember>({
      limit: 20,
      fetchItems: ({ paging, signal }) => {
        const withRetry = this._createAlertRetryWrapper(
          'Loading team members failed',
          signal,
        )
        return withRetry(() => this._fetchClientTeamMembers(paging, signal))
      },
    })

  readonly championClientTeamMembershipsScrollProvider =
    new InfiniteScrollProvider<Team>({
      limit: 20,
      fetchItems: ({ paging, signal }) => {
        const withRetry = this._createAlertRetryWrapper(
          'Loading teams failed',
          signal,
        )
        return withRetry(() =>
          this._fetchChampionClientTeamMemberships(paging, signal),
        )
      },
    })

  readonly championClientTeamMembersScrollProvider =
    new InfiniteScrollProvider<Member>({
      limit: 20,
      fetchItems: ({ paging, signal }) => {
        const withRetry = this._createAlertRetryWrapper(
          'Loading team members failed',
          signal,
        )
        return withRetry(() =>
          this._fetchChampionClientTeamMembers(paging, signal),
        )
      },
    })

  @observable
  accessor step: CreateOCRScopeJobsStep = 'ManageDocuments'

  @observable
  accessor dragging = false

  @observable
  accessor selectedFiles: File[] = []

  @observable
  accessor scopeJobMode: ScopeJobMode = 'Collectively'

  @observable
  accessor workspace: Workspace | undefined = undefined

  @observable
  accessor selectedFile: File | undefined = undefined

  @observable
  accessor legend: PublishedLegend | undefined = undefined

  @observable
  accessor legendSearchText = ''

  @observable
  accessor templates: LegendTemplate[] | undefined = undefined

  @observable
  accessor selectedTemplate: LegendTemplate | undefined = undefined

  @observable
  accessor championClient: Member | undefined = undefined

  @observable
  accessor supportingClient: Member | undefined = undefined

  @observable
  accessor clientTeamMember: TeamMember | undefined = undefined

  @observable
  accessor clientTeamSearchText = ''

  @observable
  accessor championClientTeamMembershipsSearchText = ''

  @observable
  accessor championClientTeamMembersSearchText = ''

  @observable
  accessor supportingTeamMembersSearchText = ''

  @observable
  accessor manualQuestions: Question[] | undefined = undefined

  @observable
  accessor answers: OCRManualInputAnswer[] | undefined = undefined

  @observable
  accessor selectAnswers: OCRManualInputAnswer[] = []

  @observable
  accessor saveAnswers = true

  /**
   * Whether the current user is only able to scope for themselves or team members.
   */
  @observable
  accessor isNonAdminUser = false

  @observable
  accessor selectedNonAdminUserTeam: Team | undefined = undefined

  constructor(
    private readonly _ocrScopeWorkStore: OCRScopeWorkStore,
    private readonly _searchMembers: SearchMembersFn,
    private readonly _publishedLegendStore: PublishedLegendStore,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _currentMember$: Observable<Member>,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _findTeamMembersByUserPublicId: ListTeamMembersForWorkspaceMemberFn,
    private readonly _listTeamMembers: ListTeamMembersFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.workspaceSelectorVm = new OCRScopeWorkWorkspaceSearchViewModel(
      this._getCurrentUserWorkspaceMemberships,
    )

    this.validation = validationContext(this, {
      workspace: [required('Workspace is required.')],
      legend: [required('Legend is required.')],
      selectedTemplate: [required('Template is required.')],
      championClient: [required('Champion client is required.')],
    })

    this._championClientQuery$ =
      this.championClientSearchText.debouncedValue$.pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this.championClientScrollProvider.fetch({ signal }),
          ).pipe(catchError(() => EMPTY)),
        ),
        shareLatest(),
      )

    this._supportingClientQuery$ =
      this.supportingClientSearchText.debouncedValue$.pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this.supportingClientScrollProvider.fetch({ signal }),
          ).pipe(catchError(() => EMPTY)),
        ),
        shareLatest(),
      )

    this._lifecycle
      .manage(
        this._currentMember$.pipe(
          tap((member) => {
            // Re-activating keeps the workspace the user picked; only another
            // member resets it.
            if (member === this._member) {
              return
            }
            this._member = member
            this.setWorkspace(member.workspace)
            this.tryAutoSelectTeamForUser(member)
          }),
          // Only subscribe to these if the user has the ADMIN_USER_LIST permission.
          switchMap(() =>
            this.isNonAdminUser
              ? EMPTY
              : merge(
                  this._championClientQuery$,
                  this._supportingClientQuery$,
                ).pipe(whileActive(this.modal.showing$)),
          ),
        ),
      )
      .manage(
        this.modal.showing$.pipe(
          tap(() => {
            this.tryAutoSelectLegendAndTemplate()

            if (this.isNonAdminUser) {
              this.setChampionClient(this._getCurrentMember())
            }
          }),
        ),
      )
  }

  @computed
  get userTypesSearchFilter() {
    if (this._getCurrentMember().permissions.adminUserList) {
      return ALLOWED_OCR_CLIENT_USER_ADMIN_TYPES
    }

    return ALLOWED_OCR_CLIENT_USER_TYPES
  }

  @computed
  get selectedFilesCount() {
    return this.selectedFiles.length
  }

  @computed
  get disabled() {
    return match(this.scopeJobMode, {
      Collectively: () =>
        !this.workspace ||
        !this.legend ||
        !this.selectedTemplate ||
        !this.championClient,
      Individually: () =>
        !this.selectedFile ||
        !this.workspace ||
        !this.legend ||
        !this.selectedTemplate ||
        !this.championClient,
    })
  }

  @computed
  get isOnlyOneFileSelected() {
    return this.selectedFilesCount === 1
  }

  @computed
  get workspaces() {
    return this.workspaceSelectorVm.workspacesWhereUserCanViewScopeWorkJobs
  }

  @computed
  get legends() {
    return this.legendScrollProvider.items
  }

  @computed
  get clientTeamMemberships() {
    return this.championClientTeamMembershipsScrollProvider.items
  }

  @computed
  get filteredLegends() {
    const lowerText = this.legendSearchText.toLowerCase()
    const legends = this.legends
    return legends.filter(
      (l) => !l.inactive && l.name.toLowerCase().includes(lowerText),
    )
  }

  @computed
  get canSelectLegend() {
    return this.workspace && this.legends.length !== 1
  }

  @computed
  get canSelectTemplate() {
    return (
      this.workspace &&
      this.legend &&
      this.templates &&
      this.templates.length !== 1
    )
  }

  @computed
  get canSelectClientTeam() {
    return this.workspace && this.championClient && !this.isNonAdminUser
  }

  @computed
  get canSelectManualQuestions() {
    return (
      this.selectedTemplate &&
      this.manualQuestions &&
      this.manualQuestions.length > 0
    )
  }

  @computed
  get canSaveAnswers() {
    return (
      this.selectedTemplate &&
      this.manualQuestions &&
      this.manualQuestions.length > 0 &&
      this.scopeJobMode === 'Individually'
    )
  }

  @computed
  get canSelectTeamAsNonAdminUser() {
    return (
      this.workspace &&
      this.championClient &&
      this.clientTeamMemberships.length > 1
    )
  }

  @computed
  get canSelectSupportingClientAsNonAdminUser() {
    return (
      this.workspace && this.championClient && this.selectedNonAdminUserTeam
    )
  }

  @computed
  get canSelectSupportingClientAsAdminUser() {
    return this.workspace && this.championClient
  }

  @computed
  get filteredClientTeam() {
    const lowerText = this.clientTeamSearchText.toLowerCase()
    const clientTeamItems = this.clientTeamMembersScrollProvider.items
    const clientTeam = this.selectedNonAdminUserTeam
      ? clientTeamItems.filter(
          (tm) => tm.team.id === this.selectedNonAdminUserTeam?.id,
        )
      : clientTeamItems
    return clientTeam.filter((l) =>
      l.team.name.toLowerCase().includes(lowerText),
    )
  }

  @computed
  get filteredChampionClientTeamMemberships() {
    const lowerText = this.championClientTeamMembershipsSearchText.toLowerCase()
    const clientTeam = this.championClientTeamMembershipsScrollProvider.items
    return clientTeam.filter((l) => l.name.toLowerCase().includes(lowerText))
  }

  @computed
  get filteredChampionClientTeamMembers() {
    const lowerText = this.championClientTeamMembersSearchText.toLowerCase()
    const teamMembers = this.championClientTeamMembersScrollProvider.items
    return teamMembers.filter((l) => {
      return (
        l.displayName.toLowerCase().includes(lowerText) ||
        l.email.toLowerCase().includes(lowerText)
      )
    })
  }

  @computed
  get filteredSupportingTeamMembers() {
    const lowerText = this.supportingTeamMembersSearchText.toLowerCase()
    const teamMembers =
      this.championClientTeamMembersScrollProvider.items.filter(
        (m) => m.user.publicId !== this.championClient?.user.publicId,
      )
    return teamMembers.filter((l) => {
      return (
        l.displayName.toLowerCase().includes(lowerText) ||
        l.email.toLowerCase().includes(lowerText)
      )
    })
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  setLegendSearchText(text: string) {
    this.legendSearchText = text
    if (this.legendSearchText !== '') {
      this.legendScrollProvider.more().catch(() => undefined)
    }
  }

  @action.bound
  setDragging() {
    this.dragging = true
  }

  @action.bound
  stopDragging() {
    this.dragging = false
  }

  @action.bound
  addFiles(files: FileList) {
    // Check if any of the files are unsupported.
    const unsupportedFileTypes: string[] = []
    const userFiles = Array.from(files)
    userFiles.forEach((file) => {
      const fileType = `.${file.type.split('/').pop()}`
      if (!ACCEPTED_OCR_FILE_TYPES.includes(fileType)) {
        unsupportedFileTypes.push(fileType)
      }
    })

    if (unsupportedFileTypes.length > 0) {
      this._newAlert()
        .withState('warning')
        .saying(
          `Unsupported file ${
            unsupportedFileTypes.length === 1 ? 'type' : 'types'
          } uploaded: `,
          `${unsupportedFileTypes.join(', ')}`,
        )
        .autoDismiss()
        .show()

      return
    }

    this.selectedFiles.push(...userFiles)
    if (this.selectedFiles.length > 0 && !this.modal.showing) {
      this.modal.show()
    }
  }

  @action.bound
  removeFile(fileToRemove: File) {
    this.selectedFiles = this.selectedFiles.filter(
      (file) => file !== fileToRemove,
    )
  }

  @action.bound
  goToManageDocuments() {
    this.step = 'ManageDocuments'
  }

  @action.bound
  goToScopeNewJobs() {
    if (this.isOnlyOneFileSelected) {
      this.selectedFile = this.selectedFiles[0]
    }

    this.scopeJobMode = 'Collectively'
    this.step = 'ScopeNewJobs'
  }

  @action.bound
  setIsNonAdminUser(isNonAdminUser: boolean) {
    this.isNonAdminUser = isNonAdminUser
  }

  @action.bound
  setSelectedNonAdminUserTeam(team?: Team) {
    this.selectedNonAdminUserTeam = team

    this.supportingClient = undefined
    this.clientTeamMember = undefined
    this.championClientTeamMembersScrollProvider.reset(false)
  }

  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
    this.legend = undefined
    this.selectedTemplate = undefined
    this.templates = undefined
    this.championClient = undefined
    this.supportingClient = undefined
    this.clientTeamMember = undefined

    if (this.workspace) {
      if (this.isNonAdminUser) {
        this.selectedNonAdminUserTeam = undefined
        this.championClientTeamMembershipsScrollProvider.reset(false)
        this.championClientTeamMembersScrollProvider.reset(false)

        this.tryAutoSelectTeamForUser(this._getCurrentMember(this.workspace.id))
      }

      if (!this.isNonAdminUser) {
        this.championClientScrollProvider.reset(false)
        this.supportingClientScrollProvider.reset(false)
      }

      this.clientTeamMembersScrollProvider.reset(false)
      this.tryAutoSelectLegendAndTemplate()
    }
  }

  @action.bound
  setSelectedFile(file?: File) {
    this.selectedFile = file
  }

  @action.bound
  setLegend(legend?: PublishedLegend) {
    this.legend = legend

    this.selectedTemplate = undefined
    this.templates = undefined

    if (this.legend) {
      const { versionId } = this.legend
      const signal = this._lifecycle.signal
      const withRetry = this._createAlertRetryWrapper(
        'Loading templates failed',
        signal,
      )
      withRetry(() =>
        this._ocrScopeWorkStore.fetchLegendTemplates(versionId, signal),
      )
        .then(this.setTemplates)
        .finally(() => {
          if (this.templates?.length === 1) {
            this.setSelectedTemplate(this.templates[0])
          }
        })
        .catch(() => undefined)
    }
  }

  @action.bound
  setTemplates(templates: LegendTemplate[] | undefined) {
    this.templates = templates
  }

  @action.bound
  setSelectedTemplate(template?: LegendTemplate) {
    this.selectedTemplate = template

    this.manualQuestions = undefined
    this.answers = undefined
    this.selectAnswers = []

    if (this.selectedTemplate) {
      this.fetchManualInputs().catch(() => undefined)
    }
  }

  fetchManualInputs = task.resolved(async () => {
    const signal = this._lifecycle.signal
    const withRetry = this._createAlertRetryWrapper(
      'Loading the template questions failed',
      signal,
    )
    const { questions, answers } = await withRetry(() =>
      this._ocrScopeWorkStore.fetchManualInputs({
        workspaceId: this.workspace?.id,
        legendVersionId: this.legend!.versionId,
        templateId: this.selectedTemplate?.id,
        signal,
      }),
    )
    this.setManualQuestions(questions)
    this.setAnswers(answers)
  })

  @action.bound
  setManualQuestions(questions?: Question[]) {
    this.manualQuestions = questions
  }

  @action.bound
  setAnswers(answers?: OCRManualInputAnswer[]) {
    this.answers = answers
  }

  /**
   * The current answer to a manual input question.
   */
  answerFor(questionId: Id): unknown {
    return this.answers?.find((a) => a.id === questionId)?.answer
  }

  @action.bound
  updateAnswer(answer: unknown, id?: string) {
    if (!id) {
      return
    }

    const foundAnswer = this.answers?.find((i) => i.id === id)

    if (foundAnswer) {
      foundAnswer.answer = answer
    }
  }

  @action.bound
  updatePotentialMultiSelectAnswer(
    answer: unknown,
    id?: string,
    multiSelect?: boolean,
    multiSelectValue?: boolean | SelectQuestionOption | SelectQuestionOption[],
  ) {
    if (!id) {
      return
    }

    const foundAnswer = this.answers?.find((i) => i.id === id)

    if (foundAnswer && multiSelect && Array.isArray(multiSelectValue)) {
      foundAnswer.answer = multiSelectValue

      const foundSelectAnswer = this.selectAnswers.find((i) => i.id === id)
      if (foundSelectAnswer) {
        foundSelectAnswer.answer = multiSelectValue.map((x) => x.id)
      } else {
        this.selectAnswers.push({
          id,
          answer: multiSelectValue.map((x) => x.id),
        })
      }

      return
    }

    if (foundAnswer && !Array.isArray(answer)) {
      const selectAnswer = answer as SelectQuestionOption
      foundAnswer.answer = selectAnswer
      const foundSelectAnswer = this.selectAnswers.find((i) => i.id === id)
      if (foundSelectAnswer) {
        foundSelectAnswer.answer = selectAnswer?.id || ''
      } else {
        this.selectAnswers.push({
          id,
          answer: selectAnswer.id,
        })
      }
    }
  }

  @action.bound
  setChampionClient(client?: Member) {
    // Disallow deselecting the champion client if the user is a basic user.
    if (!client && this.isNonAdminUser) {
      return
    }

    this.championClient = client

    this.supportingClient = undefined
    this.clientTeamMember = undefined

    if (this.isNonAdminUser) {
      this.championClientTeamMembersScrollProvider.reset(false)
    }

    if (!this.isNonAdminUser) {
      this.supportingClientScrollProvider.reset(false)
    }

    this.clientTeamMembersScrollProvider.reset(false)
  }

  @action.bound
  setSupportingClient(client?: Member) {
    this.supportingClient = client
  }

  @action.bound
  setClientTeamMember(team?: TeamMember) {
    this.clientTeamMember = team
  }

  @action.bound
  toggleSaveAnswers() {
    this.saveAnswers = !this.saveAnswers
  }

  @action.bound
  setClientTeamSearchText(text: string) {
    this.clientTeamSearchText = text
    if (this.clientTeamSearchText !== '') {
      this.clientTeamMembersScrollProvider.more().catch(() => undefined)
    }
  }

  @action.bound
  setChampionClientTeamMembershipsSearchText(text: string) {
    this.championClientTeamMembershipsSearchText = text
    if (this.championClientTeamMembershipsSearchText !== '') {
      this.championClientTeamMembershipsScrollProvider
        .more()
        .catch(() => undefined)
    }
  }

  @action.bound
  setChampionClientTeamMembersSearchText(text: string) {
    this.championClientTeamMembersSearchText = text
    if (this.championClientTeamMembersSearchText !== '') {
      this.championClientTeamMembersScrollProvider.more().catch(() => undefined)
    }
  }

  @action.bound
  setSupportingTeamMembersSearchText(text: string) {
    this.supportingTeamMembersSearchText = text
    if (this.supportingTeamMembersSearchText !== '') {
      this.championClientTeamMembersScrollProvider.more().catch(() => undefined)
    }
  }

  @action.bound
  onChangeScopeJobMode(mode: ScopeJobMode) {
    if (mode === 'Individually' && this.selectedFilesCount > 0) {
      this.selectedFile = this.selectedFiles[0]
    }

    this.scopeJobMode = mode
  }

  /**
   * Validates the view model.
   */
  validate() {
    this.validation.reset()

    if (!this.selectedFile && this.scopeJobMode === 'Individually') {
      this.validation.addErrors({
        selectedFile: ['A file is required.'],
      })

      return this.validation.validate().isValid
    }

    return this.validation.validate().isValid
  }

  @action.bound
  open() {
    this.modal.show()
  }

  @action.bound
  reset() {
    this.championClientSearchText.clear()
    this.supportingClientSearchText.clear()
    this.step = 'ManageDocuments'
    this.selectedFiles = []
    this.scopeJobMode = 'Collectively'
    this.selectedFile = undefined
    this.legend = undefined
    this.legendSearchText = ''
    this.templates = undefined
    this.selectedTemplate = undefined
    this.championClient = undefined
    this.supportingClient = undefined
    this.clientTeamMember = undefined
    this.clientTeamSearchText = ''
    this.championClientTeamMembershipsSearchText = ''
    this.championClientTeamMembersSearchText = ''
    this.supportingTeamMembersSearchText = ''
    this.manualQuestions = undefined
    this.answers = undefined
    this.selectAnswers = []
    this.fetchManualInputs.reset()
    this.saveAnswers = true

    this.validation.reset()
    this.submit.reset()
  }

  /**
   * Tries to auto select the legend and template when a workspace is selected.
   * If there is only one legend, it will be selected.
   * If there is only one template, it will be selected.
   */
  @action.bound
  tryAutoSelectLegendAndTemplate() {
    if (!this.workspace) {
      return
    }

    this.legendScrollProvider
      .fetch({ signal: this._lifecycle.signal })
      .then(() => {
        if (this.legends.length === 1) {
          this.setLegend(this.legends[0])
        }
      })
      .catch(() => undefined)
  }

  /**
   * Tries to auto select the team that the user belongs to.
   * Only if the user does not have the ADMIN_USER_LIST permission.
   * If there is only one team, it will be selected.
   * If there is multiple teams, the user will have to select one.
   */
  @action.bound
  tryAutoSelectTeamForUser(currentMember: Member) {
    if (
      currentMember.permissions.adminUserList ||
      currentMember.permissions.adminClientUserList
    ) {
      return
    }

    this.setIsNonAdminUser(true)
    // Set the champ client as the current user.
    this.setChampionClient(currentMember)

    this.championClientTeamMembershipsScrollProvider
      .fetch({ signal: this._lifecycle.signal })
      .then(() => {
        if (this.clientTeamMemberships.length === 1) {
          this.setSelectedNonAdminUserTeam(this.clientTeamMemberships[0])
        }
      })
      .catch(() => undefined)
  }

  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const selectedIds = new Set(this.selectAnswers.map((item) => item.id))

    const filteredAnswers = this.answers?.filter(
      ({ id }) => !selectedIds.has(id),
    )

    const data: Record<string, unknown> = {}
    filteredAnswers?.forEach(({ id, answer }) => {
      data[id] = answer
    })

    this.selectAnswers?.forEach(({ id, answer }) => {
      data[id] = answer
    })

    if (Object.values(data).length !== this.manualQuestions?.length) {
      this._newAlert()
        .withState('warning')
        .saying('Please answer all required questions')
        .autoDismiss()
        .show()

      return
    }

    const questionsWithEmptyAnswers = Object.values(data).filter(
      (value) => value === '' || (Array.isArray(value) && value.length === 0),
    )
    const isAnyAnswerEmpty = questionsWithEmptyAnswers.length > 0

    if (isAnyAnswerEmpty) {
      this._newAlert()
        .withState('warning')
        .saying(
          'Please answer all questions',
          `${questionsWithEmptyAnswers.length} unanswered questions remaining.`,
        )
        .autoDismiss()
        .show()

      return
    }

    if (this.scopeJobMode === 'Individually') {
      const isScoped = await this._createScopeWork(data, [this.selectedFile!])
        .then(() => true)
        .catch(this._createAlertErrorHandler('Scoping the job failed'))
        .catch(() => false)
      if (!isScoped) {
        return
      }

      // Remove that file from the selected files, since its already being scoped.
      this.removeFile(this.selectedFile!)

      if (this.selectedFilesCount === 0) {
        this._newAlert()
          .withState('success')
          .saying('Success!', `The job is currently being scoped.`)
          .autoDismiss()
          .show()
        this.modal.close()

        return
      }

      // Select the next file to be scoped.
      this.setSelectedFile(this.selectedFiles[0])

      this._newAlert()
        .withState('success')
        .saying(
          'Success!',
          'Job is being scoped, please continue with the next file.',
        )
        .autoDismiss()
        .show()

      // If the user doesn't want to save the answers, reset them.
      if (!this.saveAnswers) {
        this.setSelectedTemplate(this.selectedTemplate)
      }

      return
    }

    const isScoped = await this._createScopeWork(data, this.selectedFiles)
      .then(() => true)
      .catch(this._createAlertErrorHandler('Scoping the jobs failed'))
      .catch(() => false)
    if (!isScoped) {
      return
    }

    this._newAlert()
      .withState('success')
      .saying(
        'Success!',
        `The ${
          this.selectedFilesCount === 1 ? 'job is' : 'jobs are'
        } currently being scoped.`,
      )
      .autoDismiss()
      .show()

    this.modal.close()
  })

  private _createScopeWork(answers: Record<string, unknown>, files: File[]) {
    return this._ocrScopeWorkStore.createScopeWork({
      workspaceId: this.workspace?.id,
      userPublicId: this.championClient?.user.publicId,
      supportingClientUserPublicId: this.supportingClient?.user.publicId,
      teamId: this.isNonAdminUser
        ? this.selectedNonAdminUserTeam?.id
        : this.clientTeamMember?.team.id,
      legendVersionId: this.legend!.versionId,
      templateId: this.selectedTemplate?.id,
      answers,
      files,
    })
  }

  private async _searchChampionClient(
    searchText: string,
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> {
    const page = await this._searchMembers(
      {
        after: paging.after,
        limit: paging.limit,
        workspaceId: this.workspace?.id,
        searchText: searchText,
        userTypes: this.userTypesSearchFilter,
      },
      signal,
    )

    return {
      data: page.data.filter((m) => {
        const invalidCandidate = m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
      next: page.next,
      total: page.total,
    }
  }

  private async _searchSupportingClient(
    searchText: string,
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> {
    const page = await this._searchMembers(
      {
        after: paging.after,
        limit: paging.limit,
        workspaceId: this.workspace?.id,
        searchText: searchText,
        userTypes: this.userTypesSearchFilter,
      },
      signal,
    )

    return {
      data: page.data.filter((m) => {
        const invalidCandidate =
          m.user.id === 'SYSTEM_ROBOT' || m === this.championClient
        return !invalidCandidate
      }),
      next: page.next,
      total: page.total,
    }
  }

  private async _fetchClientTeamMembers(
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<TeamMember>> {
    return CursorPaged.of(
      await this._findTeamMembersByUserPublicId(
        {
          after: paging.after,
          limit: paging.limit,
          workspaceId: this.workspace?.id,
          userPublicId: this.championClient?.user.publicId,
          includeTeam: true,
        },
        signal,
      ),
    )
  }

  private async _fetchChampionClientTeamMemberships(
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Team>> {
    return CursorPaged.of(
      await this._findTeamMembersByUserPublicId(
        {
          after: paging.after,
          limit: paging.limit,
          workspaceId: this.workspace?.id,
          userPublicId: this.championClient?.user.publicId,
          includeTeam: true,
        },
        signal,
      ).then((data) => data.map((tm) => tm.team)),
    )
  }

  private async _fetchChampionClientTeamMembers(
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> {
    if (!this.selectedNonAdminUserTeam) {
      const championClient = this.championClient ?? this._getCurrentMember()
      return CursorPaged.of([championClient])
    }

    const page = await this._listTeamMembers(
      {
        after: paging.after,
        teamId: this.selectedNonAdminUserTeam.id,
        inactive: false,
        includeTeam: true,
      },
      signal,
    )

    return {
      data: page.data.map((tm) => tm.member),
      next: page.next,
      total: page.total,
    }
  }
}
